export interface TCountry {
  code: string;
  name: string;
  shortName: string;
  oksm: string;
  phoneCode: string;
  keyWords?: string[] | string;
  currency: string;

  disabled?: boolean;
}

function remapByKey<
  T extends Record<string, any>,
  K extends keyof T[keyof T],
>(
  obj: T,
  key: K,
): { [P in keyof T as T[P][K] extends string ? T[P][K] : never]: T[P] } {
  return Object.fromEntries(
    Object.values(obj).map((item) => [item[key], item]),
  );
}

/* ------- Dicts ------- */

const COUNTRIES = {
  AL: {
    code: 'AL', shortName: 'ALB', oksm: '8', name: 'Albania', phoneCode: '+355', currency: 'ALL',
  },
  DZ: {
    code: 'DZ', shortName: 'DZA', oksm: '12', name: 'Algeria', phoneCode: '+213', currency: 'DZD',
  },
  AS: {
    code: 'AS', shortName: 'ASM', oksm: '16', name: 'American Samoa', phoneCode: '+1684', currency: 'USD',
  },
  AD: {
    code: 'AD', shortName: 'AND', oksm: '20', name: 'Andorra', phoneCode: '+376', currency: 'EUR',
  },
  AO: {
    code: 'AO', shortName: 'AGO', oksm: '24', name: 'Angola', phoneCode: '+244', currency: 'AOA',
  },
  AI: {
    code: 'AI', shortName: 'AIA', oksm: '660', name: 'Anguilla', phoneCode: '+1264', currency: 'XCD',
  },
  AG: {
    code: 'AG', shortName: 'ATG', oksm: '28', name: 'Antigua and Barbuda', phoneCode: '+1268', currency: 'XCD',
  },
  AR: {
    code: 'AR', shortName: 'ARG', oksm: '32', name: 'Argentina', phoneCode: '+54', currency: 'ARS',
  },
  AM: {
    code: 'AM', shortName: 'ARM', oksm: '51', name: 'Armenia', phoneCode: '+374', currency: 'AMD',
  },
  AU: {
    code: 'AU', shortName: 'AUS', oksm: '36', name: 'Australia', phoneCode: '+61', currency: 'AUD',
  },
  AT: {
    code: 'AT', shortName: 'AUT', oksm: '40', name: 'Austria', phoneCode: '+43', currency: 'EUR',
  },
  AZ: {
    code: 'AZ', shortName: 'AZE', oksm: '31', name: 'Azerbaijan', phoneCode: '+994', currency: 'AZN',
  },
  BS: {
    code: 'BS', shortName: 'BHS', oksm: '44', name: 'Bahamas', phoneCode: '+1242', currency: 'BSD',
  },
  BH: {
    code: 'BH', shortName: 'BHR', oksm: '48', name: 'Bahrain', phoneCode: '+973', currency: 'BHD',
  },
  BD: {
    code: 'BD', shortName: 'BGD', oksm: '50', name: 'Bangladesh', phoneCode: '+880', currency: 'BDT',
  },
  BB: {
    code: 'BB', shortName: 'BRB', oksm: '52', name: 'Barbados', phoneCode: '+1246', currency: 'BBD',
  },
  BE: {
    code: 'BE', shortName: 'BEL', oksm: '56', name: 'Belgium', phoneCode: '+32', currency: 'EUR',
  },
  BY: {
    code: 'BY', shortName: 'BLR', oksm: '112', name: 'Belarus', phoneCode: '+375', currency: 'BYN',
  },
  BZ: {
    code: 'BZ', shortName: 'BLZ', oksm: '84', name: 'Belize', phoneCode: '+501', currency: 'BZD',
  },
  BJ: {
    code: 'BJ', shortName: 'BEN', oksm: '204', name: 'Benin', phoneCode: '+229', currency: 'XOF',
  },
  BM: {
    code: 'BM', shortName: 'BMU', oksm: '60', name: 'Bermuda', phoneCode: '+1441', currency: 'BMD',
  },
  BT: {
    code: 'BT', shortName: 'BTN', oksm: '64', name: 'Bhutan', phoneCode: '+975', currency: 'BTN',
  },
  BQ: {
    code: 'BQ', shortName: 'BES', oksm: '535', name: 'Bonaire, Sint Eustatius and Saba', phoneCode: '+599', currency: 'USD',
  },
  BW: {
    code: 'BW', shortName: 'BWA', oksm: '72', name: 'Botswana', phoneCode: '+267', currency: 'BWP',
  },
  BV: {
    code: 'BV', shortName: 'BVT', oksm: '74', name: 'Bouvet Island', phoneCode: '+47', currency: 'NOK',
  },
  BR: {
    code: 'BR', shortName: 'BRA', oksm: '76', name: 'Brazil', phoneCode: '+55', currency: 'BRL',
  },
  IO: {
    code: 'IO', shortName: 'IOT', oksm: '86', name: 'British Indian Ocean Territory', phoneCode: '+246', currency: 'USD',
  },
  VG: {
    code: 'VG', shortName: 'VGB', oksm: '92', name: 'British Virgin Islands', phoneCode: '+1284', currency: 'USD',
  },
  BN: {
    code: 'BN', shortName: 'BRN', oksm: '96', name: 'Brunei Darussalam', phoneCode: '+673', currency: 'BND',
  },
  BG: {
    code: 'BG', shortName: 'BGR', oksm: '100', name: 'Bulgaria', phoneCode: '+359', currency: 'BGN',
  },
  BF: {
    code: 'BF', shortName: 'BFA', oksm: '854', name: 'Burkina Faso', phoneCode: '+226', currency: 'XOF',
  },
  KH: {
    code: 'KH', shortName: 'KHM', oksm: '116', name: 'Cambodia', phoneCode: '+855', currency: 'KHR',
  },
  CM: {
    code: 'CM', shortName: 'CMR', oksm: '120', name: 'Cameroon', phoneCode: '+237', currency: 'XAF',
  },
  CA: {
    code: 'CA', shortName: 'CAN', oksm: '124', name: 'Canada', phoneCode: '+1', currency: 'CAD',
  },
  CV: {
    code: 'CV', shortName: 'CPV', oksm: '132', name: 'Cape Verde', phoneCode: '+238', currency: 'CVE',
  },
  KY: {
    code: 'KY', shortName: 'CYM', oksm: '136', name: 'Cayman Islands', phoneCode: '+345', currency: 'KYD',
  },
  TD: {
    code: 'TD', shortName: 'TCD', oksm: '148', name: 'Chad', phoneCode: '+235', currency: 'XAF',
  },
  CL: {
    code: 'CL', shortName: 'CHL', oksm: '152', name: 'Chile', phoneCode: '+56', currency: 'CLP',
  },
  CN: {
    code: 'CN', shortName: 'CHN', oksm: '156', name: 'China', phoneCode: '+86', currency: 'CNY',
  },
  CX: {
    code: 'CX', shortName: 'CXR', oksm: '162', name: 'Christmas Island', phoneCode: '+61', currency: 'AUD',
  },
  CC: {
    code: 'CC', shortName: 'CCK', oksm: '166', name: 'Cocos Islands', phoneCode: '+61', currency: 'AUD',
  },
  CO: {
    code: 'CO', shortName: 'COL', oksm: '170', name: 'Colombia', phoneCode: '+57', currency: 'COP',
  },
  KM: {
    code: 'KM', shortName: 'COM', oksm: '174', name: 'Comoros', phoneCode: '+269', currency: 'KMF',
  },
  CK: {
    code: 'CK', shortName: 'COK', oksm: '184', name: 'Cook Islands', phoneCode: '+682', currency: 'NZD',
  },
  CR: {
    code: 'CR', shortName: 'CRI', oksm: '188', name: 'Costa Rica', phoneCode: '+506', currency: 'CRC',
  },
  CI: {
    code: 'CI', shortName: 'CIV', oksm: '384', name: 'Côte d\'Ivoire', phoneCode: '+225', currency: 'XOF',
  },
  HR: {
    code: 'HR', shortName: 'HRV', oksm: '191', name: 'Croatia', phoneCode: '+385', currency: 'HRK',
  },
  CU: {
    code: 'CU', shortName: 'CUB', oksm: '192', name: 'Cuba', phoneCode: '+53', currency: 'CUP',
  },
  CY: {
    code: 'CY', shortName: 'CYP', oksm: '196', name: 'Cyprus', phoneCode: '+357', currency: 'EUR',
  },
  CZ: {
    code: 'CZ', shortName: 'CZE', oksm: '203', name: 'Czech Republic', phoneCode: '+420', currency: 'CZK',
  },
  DK: {
    code: 'DK', shortName: 'DNK', oksm: '208', name: 'Denmark', phoneCode: '+45', currency: 'DKK',
  },
  DJ: {
    code: 'DJ', shortName: 'DJI', oksm: '262', name: 'Djibouti', phoneCode: '+253', currency: 'DJF',
  },
  DM: {
    code: 'DM', shortName: 'DMA', oksm: '212', name: 'Dominica', phoneCode: '+1767', currency: 'XCD',
  },
  DO: {
    code: 'DO', shortName: 'DOM', oksm: '214', name: 'Dominican Republic', phoneCode: '+1849', currency: 'DOP',
  },
  EC: {
    code: 'EC', shortName: 'ECU', oksm: '218', name: 'Ecuador', phoneCode: '+593', currency: 'USD',
  },
  EG: {
    code: 'EG', shortName: 'EGY', oksm: '818', name: 'Egypt', phoneCode: '+20', currency: 'EGP',
  },
  SV: {
    code: 'SV', shortName: 'SLV', oksm: '222', name: 'El Salvador', phoneCode: '+503', currency: 'USD',
  },
  GQ: {
    code: 'GQ', shortName: 'GNQ', oksm: '226', name: 'Equatorial Guinea', phoneCode: '+240', currency: 'XAF',
  },
  ER: {
    code: 'ER', shortName: 'ERI', oksm: '232', name: 'Eritrea', phoneCode: '+291', currency: 'ERN',
  },
  EE: {
    code: 'EE', shortName: 'EST', oksm: '233', name: 'Estonia', phoneCode: '+372', currency: 'EUR',
  },
  ET: {
    code: 'ET', shortName: 'ETH', oksm: '231', name: 'Ethiopia', phoneCode: '+251', currency: 'ETB',
  },
  FK: {
    code: 'FK', shortName: 'FLK', oksm: '238', name: 'Falkland Islands', phoneCode: '+500', currency: 'FKP',
  },
  FO: {
    code: 'FO', shortName: 'FRO', oksm: '234', name: 'Faroe Islands', phoneCode: '+298', currency: 'DKK',
  },
  FM: {
    code: 'FM', shortName: 'FSM', oksm: '583', name: 'Federated States of Micronesia', phoneCode: '+691', currency: 'USD',
  },
  FJ: {
    code: 'FJ', shortName: 'FJI', oksm: '242', name: 'Fiji', phoneCode: '+679', currency: 'FJD',
  },
  FI: {
    code: 'FI', shortName: 'FIN', oksm: '246', name: 'Finland', phoneCode: '+358', currency: 'EUR',
  },
  FR: {
    code: 'FR', shortName: 'FRA', oksm: '250', name: 'France', phoneCode: '+33', currency: 'EUR',
  },
  GF: {
    code: 'GF', shortName: 'GUF', oksm: '254', name: 'French Guiana', phoneCode: '+594', currency: 'EUR',
  },
  PF: {
    code: 'PF', shortName: 'PYF', oksm: '258', name: 'French Polynesia', phoneCode: '+689', currency: 'XPF',
  },
  TF: {
    code: 'TF', shortName: 'ATF', oksm: '260', name: 'French Southern Territories', phoneCode: '+262', currency: 'EUR',
  },
  GA: {
    code: 'GA', shortName: 'GAB', oksm: '266', name: 'Gabon', phoneCode: '+241', currency: 'XAF',
  },
  GM: {
    code: 'GM', shortName: 'GMB', oksm: '270', name: 'Gambia', phoneCode: '+220', currency: 'GMD',
  },
  GE: {
    code: 'GE', shortName: 'GEO', oksm: '268', name: 'Georgia', phoneCode: '+995', currency: 'GEL',
  },
  DE: {
    code: 'DE', shortName: 'DEU', oksm: '276', name: 'Germany', phoneCode: '+49', currency: 'EUR',
  },
  GH: {
    code: 'GH', shortName: 'GHA', oksm: '288', name: 'Ghana', phoneCode: '+233', currency: 'GHS',
  },
  GI: {
    code: 'GI', shortName: 'GIB', oksm: '292', name: 'Gibraltar', phoneCode: '+350', currency: 'GIP',
  },
  GR: {
    code: 'GR', shortName: 'GRC', oksm: '300', name: 'Greece', phoneCode: '+30', currency: 'EUR',
  },
  GL: {
    code: 'GL', shortName: 'GRL', oksm: '304', name: 'Greenland', phoneCode: '+299', currency: 'DKK',
  },
  GD: {
    code: 'GD', shortName: 'GRD', oksm: '308', name: 'Grenada', phoneCode: '+1473', currency: 'XCD',
  },
  GP: {
    code: 'GP', shortName: 'GLP', oksm: '312', name: 'Guadeloupe', phoneCode: '+590', currency: 'EUR',
  },
  GU: {
    code: 'GU', shortName: 'GUM', oksm: '316', name: 'Guam', phoneCode: '+1671', currency: 'USD',
  },
  GT: {
    code: 'GT', shortName: 'GTM', oksm: '320', name: 'Guatemala', phoneCode: '+502', currency: 'GTQ',
  },
  GG: {
    code: 'GG', shortName: 'GGY', oksm: '831', name: 'Guernsey', phoneCode: '+44', currency: 'GBP',
  },
  GY: {
    code: 'GY', shortName: 'GUY', oksm: '328', name: 'Guyana', phoneCode: '+592', currency: 'GYD',
  },
  HM: {
    code: 'HM', shortName: 'HMD', oksm: '334', name: 'Heard Island and McDonald Islands', phoneCode: '+672', currency: 'AUD',
  },
  VA: {
    code: 'VA', shortName: 'VAT', oksm: '336', name: 'Vatican City', phoneCode: '+379', currency: 'EUR',
  },
  HN: {
    code: 'HN', shortName: 'HND', oksm: '340', name: 'Honduras', phoneCode: '+504', currency: 'HNL',
  },
  HK: {
    code: 'HK', shortName: 'HKG', oksm: '344', name: 'Hong Kong', phoneCode: '+852', currency: 'HKD',
  },
  HU: {
    code: 'HU', shortName: 'HUN', oksm: '348', name: 'Hungary', phoneCode: '+36', currency: 'HUF',
  },
  IS: {
    code: 'IS', shortName: 'ISL', oksm: '352', name: 'Iceland', phoneCode: '+354', currency: 'ISK',
  },
  IN: {
    code: 'IN', shortName: 'IND', oksm: '356', name: 'India', phoneCode: '+91', currency: 'INR',
  },
  ID: {
    code: 'ID', shortName: 'IDN', oksm: '360', name: 'Indonesia', phoneCode: '+62', currency: 'IDR',
  },
  IE: {
    code: 'IE', shortName: 'IRL', oksm: '372', name: 'Ireland', phoneCode: '+353', currency: 'EUR',
  },
  IM: {
    code: 'IM', shortName: 'IMN', oksm: '833', name: 'Isle of Man', phoneCode: '+44', currency: 'GBP',
  },
  IL: {
    code: 'IL', shortName: 'ISR', oksm: '376', name: 'Israel', phoneCode: '+972', currency: 'ILS',
  },
  IT: {
    code: 'IT', shortName: 'ITA', oksm: '380', name: 'Italy', phoneCode: '+39', currency: 'EUR',
  },
  JM: {
    code: 'JM', shortName: 'JAM', oksm: '388', name: 'Jamaica', phoneCode: '+1876', currency: 'JMD',
  },
  JP: {
    code: 'JP', shortName: 'JPN', oksm: '392', name: 'Japan', phoneCode: '+81', currency: 'JPY',
  },
  JE: {
    code: 'JE', shortName: 'JEY', oksm: '832', name: 'Jersey', phoneCode: '+44', currency: 'GBP',
  },
  JO: {
    code: 'JO', shortName: 'JOR', oksm: '400', name: 'Jordan', phoneCode: '+962', currency: 'JOD',
  },
  KZ: {
    code: 'KZ', shortName: 'KAZ', oksm: '398', name: 'Kazakhstan', phoneCode: '+7', currency: 'KZT',
  },
  KE: {
    code: 'KE', shortName: 'KEN', oksm: '404', name: 'Kenya', phoneCode: '+254', currency: 'KES',
  },
  KI: {
    code: 'KI', shortName: 'KIR', oksm: '296', name: 'Kiribati', phoneCode: '+686', currency: 'AUD',
  },
  KW: {
    code: 'KW', shortName: 'KWT', oksm: '414', name: 'Kuwait', phoneCode: '+965', currency: 'KWD',
  },
  KG: {
    code: 'KG', shortName: 'KGZ', oksm: '417', name: 'Kyrgyzstan', phoneCode: '+996', currency: 'KGS',
  },
  LA: {
    code: 'LA', shortName: 'LAO', oksm: '418', name: 'Lao People\'s Democratic Republic', phoneCode: '+856', currency: 'LAK',
  },
  LV: {
    code: 'LV', shortName: 'LVA', oksm: '428', name: 'Latvia', phoneCode: '+371', currency: 'EUR',
  },
  LS: {
    code: 'LS', shortName: 'LSO', oksm: '426', name: 'Lesotho', phoneCode: '+266', currency: 'LSL',
  },
  LR: {
    code: 'LR', shortName: 'LBR', oksm: '430', name: 'Liberia', phoneCode: '+231', currency: 'LRD',
  },
  LI: {
    code: 'LI', shortName: 'LIE', oksm: '438', name: 'Liechtenstein', phoneCode: '+423', currency: 'CHF',
  },
  LT: {
    code: 'LT', shortName: 'LTU', oksm: '440', name: 'Lithuania', phoneCode: '+370', currency: 'EUR',
  },
  LU: {
    code: 'LU', shortName: 'LUX', oksm: '442', name: 'Luxembourg', phoneCode: '+352', currency: 'EUR',
  },
  MO: {
    code: 'MO', shortName: 'MCO', oksm: '492', name: 'Macao', phoneCode: '+853', currency: 'MOP',
  },
  MG: {
    code: 'MG', shortName: 'MDG', oksm: '450', name: 'Madagascar', phoneCode: '+261', currency: 'MGA',
  },
  MW: {
    code: 'MW', shortName: 'MWI', oksm: '454', name: 'Malawi', phoneCode: '+265', currency: 'MWK',
  },
  MY: {
    code: 'MY', shortName: 'MYS', oksm: '458', name: 'Malaysia', phoneCode: '+60', currency: 'MYR',
  },
  MV: {
    code: 'MV', shortName: 'MDV', oksm: '462', name: 'Maldives', phoneCode: '+960', currency: 'MVR',
  },
  MT: {
    code: 'MT', shortName: 'MLT', oksm: '470', name: 'Malta', phoneCode: '+356', currency: 'EUR',
  },
  MH: {
    code: 'MH', shortName: 'MHL', oksm: '584', name: 'Marshall Islands', phoneCode: '+692', currency: 'USD',
  },
  MQ: {
    code: 'MQ', shortName: 'MTQ', oksm: '474', name: 'Martinique', phoneCode: '+596', currency: 'EUR',
  },
  MR: {
    code: 'MR', shortName: 'MRT', oksm: '478', name: 'Mauritania', phoneCode: '+222', currency: 'MRU',
  },
  MU: {
    code: 'MU', shortName: 'MUS', oksm: '480', name: 'Mauritius', phoneCode: '+230', currency: 'MUR',
  },
  YT: {
    code: 'YT', shortName: 'MYT', oksm: '175', name: 'Mayotte', phoneCode: '+262', currency: 'EUR',
  },
  MX: {
    code: 'MX', shortName: 'MEX', oksm: '484', name: 'Mexico', phoneCode: '+52', currency: 'MXN',
  },
  MC: {
    code: 'MC', shortName: 'MCO', oksm: '492', name: 'Monaco', phoneCode: '+377', currency: 'EUR',
  },
  MN: {
    code: 'MN', shortName: 'MNG', oksm: '496', name: 'Mongolia', phoneCode: '+976', currency: 'MNT',
  },
  ME: {
    code: 'ME', shortName: 'MNE', oksm: '499', name: 'Montenegro', phoneCode: '+382', currency: 'EUR',
  },
  MS: {
    code: 'MS', shortName: 'MSR', oksm: '500', name: 'Montserrat', phoneCode: '+1664', currency: 'XCD',
  },
  MA: {
    code: 'MA', shortName: 'MAR', oksm: '504', name: 'Morocco', phoneCode: '+212', currency: 'MAD',
  },
  MZ: {
    code: 'MZ', shortName: 'MOZ', oksm: '508', name: 'Mozambique', phoneCode: '+258', currency: 'MZN',
  },
  NA: {
    code: 'NA', shortName: 'NAM', oksm: '516', name: 'Namibia', phoneCode: '+264', currency: 'NAD',
  },
  NR: {
    code: 'NR', shortName: 'NRU', oksm: '520', name: 'Nauru', phoneCode: '+674', currency: 'AUD',
  },
  NP: {
    code: 'NP', shortName: 'NPL', oksm: '524', name: 'Nepal', phoneCode: '+977', currency: 'NPR',
  },
  NL: {
    code: 'NL', shortName: 'NLD', oksm: '528', name: 'Netherlands', phoneCode: '+31', currency: 'EUR',
  },
  NC: {
    code: 'NC', shortName: 'NCL', oksm: '540', name: 'New Caledonia', phoneCode: '+687', currency: 'XPF',
  },
  NZ: {
    code: 'NZ', shortName: 'NZL', oksm: '554', name: 'New Zealand', phoneCode: '+64', currency: 'NZD',
  },
  NE: {
    code: 'NE', shortName: 'NER', oksm: '562', name: 'Niger', phoneCode: '+227', currency: 'XOF',
  },
  NG: {
    code: 'NG', shortName: 'NGA', oksm: '566', name: 'Nigeria', phoneCode: '+234', currency: 'NGN',
  },
  NU: {
    code: 'NU', shortName: 'NIU', oksm: '570', name: 'Niue', phoneCode: '+683', currency: 'NZD',
  },
  NF: {
    code: 'NF', shortName: 'NFK', oksm: '574', name: 'Norfolk Island', phoneCode: '+672', currency: 'AUD',
  },
  MP: {
    code: 'MP', shortName: 'MNP', oksm: '580', name: 'Northern Mariana Islands', phoneCode: '+1670', currency: 'USD',
  },
  NO: {
    code: 'NO', shortName: 'NOR', oksm: '578', name: 'Norway', phoneCode: '+47', currency: 'NOK',
  },
  OM: {
    code: 'OM', shortName: 'OMN', oksm: '512', name: 'Oman', phoneCode: '+968', currency: 'OMR',
  },
  PK: {
    code: 'PK', shortName: 'PAK', oksm: '586', name: 'Pakistan', phoneCode: '+92', currency: 'PKR',
  },
  PW: {
    code: 'PW', shortName: 'PLW', oksm: '585', name: 'Palau', phoneCode: '+680', currency: 'USD',
  },
  PA: {
    code: 'PA', shortName: 'PAN', oksm: '591', name: 'Panama', phoneCode: '+507', currency: 'PAB',
  },
  PG: {
    code: 'PG', shortName: 'PNG', oksm: '598', name: 'Papua New Guinea', phoneCode: '+675', currency: 'PGK',
  },
  PY: {
    code: 'PY', shortName: 'PRY', oksm: '600', name: 'Paraguay', phoneCode: '+595', currency: 'PYG',
  },
  PE: {
    code: 'PE', shortName: 'PER', oksm: '604', name: 'Peru', phoneCode: '+51', currency: 'PEN',
  },
  PH: {
    code: 'PH', shortName: 'PHL', oksm: '608', name: 'Philippines', phoneCode: '+63', currency: 'PHP',
  },
  PN: {
    code: 'PN', shortName: 'PCN', oksm: '612', name: 'Pitcairn', phoneCode: '+64', currency: 'NZD',
  },
  BO: {
    code: 'BO', shortName: 'BOL', oksm: '68', name: 'Plurinational State of Bolivia', phoneCode: '+591', currency: 'BOB',
  },
  PL: {
    code: 'PL', shortName: 'POL', oksm: '616', name: 'Poland', phoneCode: '+48', currency: 'PLN',
  },
  PT: {
    code: 'PT', shortName: 'PRT', oksm: '620', name: 'Portugal', phoneCode: '+351', currency: 'EUR',
  },
  PR: {
    code: 'PR', shortName: 'PRI', oksm: '630', name: 'Puerto Rico', phoneCode: '+1939', currency: 'USD',
  },
  QA: {
    code: 'QA', shortName: 'QAT', oksm: '634', name: 'Qatar', phoneCode: '+974', currency: 'QAR',
  },
  KR: {
    code: 'KR', shortName: 'KOR', oksm: '410', name: 'Republic of Korea', phoneCode: '+82', currency: 'KRW',
  },
  MD: {
    code: 'MD', shortName: 'MDA', oksm: '498', name: 'Republic of Moldova', phoneCode: '+373', currency: 'MDL',
  },
  RO: {
    code: 'RO', shortName: 'ROU', oksm: '642', name: 'Romania', phoneCode: '+40', currency: 'RON',
  },
  RU: {
    code: 'RU', shortName: 'RUS', oksm: '643', name: 'Russia', phoneCode: '+7', currency: 'RUB',
  },
  RW: {
    code: 'RW', shortName: 'RWA', oksm: '646', name: 'Rwanda', phoneCode: '+250', currency: 'RWF',
  },
  BL: {
    code: 'BL', shortName: 'BLM', oksm: '652', name: 'Saint Barthélemy', phoneCode: '+590', currency: 'EUR',
  },
  SH: {
    code: 'SH', shortName: 'SHN', oksm: '654', name: 'Saint Helena, Ascension and Tristan da Cunha', phoneCode: '+290', currency: 'SHP',
  },
  KN: {
    code: 'KN', shortName: 'KNA', oksm: '659', name: 'Saint Kitts and Nevis', phoneCode: '+1869', currency: 'XCD',
  },
  LC: {
    code: 'LC', shortName: 'LCA', oksm: '662', name: 'Saint Lucia', phoneCode: '+1758', currency: 'XCD',
  },
  MF: {
    code: 'MF', shortName: 'MAF', oksm: '663', name: 'Saint Martin', phoneCode: '+590', currency: 'EUR',
  },
  PM: {
    code: 'PM', shortName: 'SPM', oksm: '666', name: 'Saint Pierre and Miquelon', phoneCode: '+508', currency: 'EUR',
  },
  VC: {
    code: 'VC', shortName: 'VCT', oksm: '670', name: 'Saint Vincent and the Grenadines', phoneCode: '+1784', currency: 'XCD',
  },
  WS: {
    code: 'WS', shortName: 'WSM', oksm: '882', name: 'Samoa', phoneCode: '+685', currency: 'WST',
  },
  SM: {
    code: 'SM', shortName: 'SMR', oksm: '674', name: 'San Marino', phoneCode: '+378', currency: 'EUR',
  },
  ST: {
    code: 'ST', shortName: 'STP', oksm: '678', name: 'Sao Tome and Principe', phoneCode: '+239', currency: 'STN',
  },
  SA: {
    code: 'SA', shortName: 'SAU', oksm: '682', name: 'Saudi Arabia', phoneCode: '+966', currency: 'SAR',
  },
  SN: {
    code: 'SN', shortName: 'SEN', oksm: '686', name: 'Senegal', phoneCode: '+221', currency: 'XOF',
  },
  RS: {
    code: 'RS', shortName: 'SRB', oksm: '688', name: 'Serbia', phoneCode: '+381', currency: 'RSD',
  },
  SC: {
    code: 'SC', shortName: 'SYC', oksm: '690', name: 'Seychelles', phoneCode: '+248', currency: 'SCR',
  },
  SL: {
    code: 'SL', shortName: 'SLE', oksm: '694', name: 'Sierra Leone', phoneCode: '+232', currency: 'SLL',
  },
  SG: {
    code: 'SG', shortName: 'SGP', oksm: '702', name: 'Singapore', phoneCode: '+65', currency: 'SGD',
  },
  SX: {
    code: 'SX', shortName: 'SXM', oksm: '534', name: 'Sint Maarten', phoneCode: '+721', currency: 'ANG',
  },
  SK: {
    code: 'SK', shortName: 'SVK', oksm: '703', name: 'Slovakia', phoneCode: '+421', currency: 'EUR',
  },
  SI: {
    code: 'SI', shortName: 'SVN', oksm: '705', name: 'Slovenia', phoneCode: '+386', currency: 'EUR',
  },
  SB: {
    code: 'SB', shortName: 'SLB', oksm: '90', name: 'Solomon Islands', phoneCode: '+677', currency: 'SBD',
  },
  ZA: {
    code: 'ZA', shortName: 'ZAF', oksm: '710', name: 'South Africa', phoneCode: '+27', currency: 'ZAR',
  },
  GS: {
    code: 'GS', shortName: 'SGS', oksm: '239', name: 'South Georgia and the South Sandwich Islands', phoneCode: '+500', currency: 'GBP',
  },
  ES: {
    code: 'ES', shortName: 'ESP', oksm: '724', name: 'Spain', phoneCode: '+34', currency: 'EUR',
  },
  LK: {
    code: 'LK', shortName: 'LKA', oksm: '144', name: 'Sri Lanka', phoneCode: '+94', currency: 'LKR',
  },
  SR: {
    code: 'SR', shortName: 'SUR', oksm: '740', name: 'Suriname', phoneCode: '+597', currency: 'SRD',
  },
  SJ: {
    code: 'SJ', shortName: 'SJM', oksm: '744', name: 'Svalbard and Jan Mayen', phoneCode: '+47', currency: 'NOK',
  },
  SE: {
    code: 'SE', shortName: 'SWE', oksm: '752', name: 'Sweden', phoneCode: '+46', currency: 'SEK',
  },
  CH: {
    code: 'CH', shortName: 'CHE', oksm: '756', name: 'Switzerland', phoneCode: '+41', currency: 'CHF',
  },
  TW: {
    code: 'TW', shortName: 'TWN', oksm: '158', name: 'Taiwan, Province of China', phoneCode: '+886', currency: 'TWD',
  },
  TJ: {
    code: 'TJ', shortName: 'TJK', oksm: '762', name: 'Tajikistan', phoneCode: '+992', currency: 'TJS',
  },
  TH: {
    code: 'TH', shortName: 'THA', oksm: '764', name: 'Thailand', phoneCode: '+66', currency: 'THB',
  },
  MK: {
    code: 'MK', shortName: 'MKD', oksm: '807', name: 'North Macedonia', phoneCode: '+389', currency: 'MKD',
  },
  TL: {
    code: 'TL', shortName: 'TLS', oksm: '626', name: 'Timor-Leste', phoneCode: '+670', currency: 'USD',
  },
  TG: {
    code: 'TG', shortName: 'TGO', oksm: '768', name: 'Togo', phoneCode: '+228', currency: 'XOF',
  },
  TK: {
    code: 'TK', shortName: 'TKL', oksm: '772', name: 'Tokelau', phoneCode: '+690', currency: 'NZD',
  },
  TO: {
    code: 'TO', shortName: 'TON', oksm: '776', name: 'Tonga', phoneCode: '+676', currency: 'TOP',
  },
  TT: {
    code: 'TT', shortName: 'TTO', oksm: '780', name: 'Trinidad and Tobago', phoneCode: '+1868', currency: 'TTD',
  },
  TN: {
    code: 'TN', shortName: 'TUN', oksm: '788', name: 'Tunisia', phoneCode: '+216', currency: 'TND',
  },
  TR: {
    code: 'TR', shortName: 'TUR', oksm: '792', name: 'Turkey', phoneCode: '+90', currency: 'TRY',
  },
  TC: {
    code: 'TC', shortName: 'TCA', oksm: '796', name: 'Turks and Caicos Islands', phoneCode: '+1649', currency: 'USD',
  },
  TV: {
    code: 'TV', shortName: 'TUV', oksm: '798', name: 'Tuvalu', phoneCode: '+688', currency: 'AUD',
  },
  UG: {
    code: 'UG', shortName: 'UGA', oksm: '800', name: 'Uganda', phoneCode: '+256', currency: 'UGX',
  },
  UA: {
    code: 'UA', shortName: 'UKR', oksm: '804', name: 'Ukraine', phoneCode: '+380', currency: 'UAH',
  },
  AE: {
    code: 'AE', shortName: 'ARE', oksm: '784', name: 'United Arab Emirates', phoneCode: '+971', currency: 'AED',
  },
  GB: {
    code: 'GB', shortName: 'GBR', oksm: '826', name: 'United Kingdom', phoneCode: '+44', currency: 'GBP', keyWords: ['UK', 'Great', 'Britain'],
  },
  TZ: {
    code: 'TZ', shortName: 'TZA', oksm: '834', name: 'United Republic of Tanzania', phoneCode: '+255', currency: 'TZS',
  },
  US: {
    code: 'US', shortName: 'USA', oksm: '840', name: 'United States', phoneCode: '+1', currency: 'USD',
  },
  UY: {
    code: 'UY', shortName: 'URY', oksm: '858', name: 'Uruguay', phoneCode: '+598', currency: 'UYU',
  },
  UZ: {
    code: 'UZ', shortName: 'UZB', oksm: '860', name: 'Uzbekistan', phoneCode: '+998', currency: 'UZS',
  },
  VU: {
    code: 'VU', shortName: 'VUT', oksm: '548', name: 'Vanuatu', phoneCode: '+678', currency: 'VUV',
  },
  VN: {
    code: 'VN', shortName: 'VNM', oksm: '704', name: 'Viet Nam', phoneCode: '+84', currency: 'VND',
  },
  VI: {
    code: 'VI', shortName: 'VIR', oksm: '850', name: 'Virgin Islands, U.S.', phoneCode: '+1340', currency: 'USD',
  },
  WF: {
    code: 'WF', shortName: 'WLF', oksm: '876', name: 'Wallis and Futuna', phoneCode: '+681', currency: 'XPF',
  },
  EH: {
    code: 'EH', shortName: 'ESH', oksm: '732', name: 'Western Sahara', phoneCode: '+212', currency: 'MAD',
  },
  ZM: {
    code: 'ZM', shortName: 'ZMB', oksm: '894', name: 'Zambia', phoneCode: '+260', currency: 'ZMK',
  },
} as const satisfies Record<string, TCountry>;

const COUNTRIES_BY_SHORT_NAME = remapByKey(COUNTRIES, 'shortName');

export type CountryCode = keyof typeof COUNTRIES;
export type CountryShortName = keyof typeof COUNTRIES_BY_SHORT_NAME;

/* ------- Arrays utils ------- */

function getCountriesByCode(codesToPick: CountryCode[]): TCountry[] {
  return codesToPick.map((code) => COUNTRIES[code]);
}

function getCountriesExcept(countries: TCountry[], codesToOmit: CountryCode[]): TCountry[] {
  const countriesToOmit = new Set<string>(codesToOmit);
  return countries.filter(({ code }) => !countriesToOmit.has(code));
}

function makeDisabledExcept(countries: TCountry[], codesOfActiveCountries: CountryCode[]): TCountry[] {
  const activeCountryCodesSet = new Set<string>(codesOfActiveCountries);
  const grouped = Object.groupBy(countries, ({ code }) => (activeCountryCodesSet.has(code) ? 'active' : 'disabled'));
  return [
    ...(grouped.active || []),
    ...(grouped.disabled || []).map((country) => ({ ...country, disabled: true })),
  ];
}

/* ------- Codes ------- */

export const EEA_COUNTRIES_CODES: CountryCode[] = [
  'AD',
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IS',
  'IE',
  'IT',
  'LV',
  'LI',
  'LT',
  'LU',
  'MT',
  'MC',
  'NL',
  'NO',
  'PL',
  'PT',
  'MD',
  'RO',
  'SM',
  'SK',
  'SI',
  'ES',
  'SE',
  'CH',
  'GB',
  'VA',
];

const UNPERMITTED_COUNTRIES_CODES_FOR_CARD_ISSUE = new Set<CountryCode>(['AD', 'IE', 'MC', 'SM', 'VA']);

const COUNTRIES_CODES_FOR_CARD_ISSUE = EEA_COUNTRIES_CODES.filter((code) => !UNPERMITTED_COUNTRIES_CODES_FOR_CARD_ISSUE.has(code));

/* ------- Arrays ------- */

export const ALL_COUNTRIES: TCountry[] = Object.values(COUNTRIES);

export const PERMITTED_COUNTRIES = getCountriesExcept(ALL_COUNTRIES, ['RU']);

export const EEA_COUNTRIES = getCountriesByCode(EEA_COUNTRIES_CODES);

export const EEA_COUNTRIES_WITH_DISABLED = makeDisabledExcept(ALL_COUNTRIES, EEA_COUNTRIES_CODES);

export const CARD_ISSUE_COUNTRIES = makeDisabledExcept(ALL_COUNTRIES, COUNTRIES_CODES_FOR_CARD_ISSUE);

/* ------- Utils ------- */
// DEBT: Move utils to utils

export function getCountryByCode(code?: string): TCountry | undefined {
  // DEBT: The old dictionary has keys for `code` and `shortName`, we need to check that the function is used to search for `code`
  return COUNTRIES[code as CountryCode] ?? COUNTRIES_BY_SHORT_NAME[code as CountryShortName];
}

export function getCountryByShortName(shortName?: string): TCountry | undefined {
  // DEBT: The old dictionary has keys for `code` and `shortName`, we need to check that the function is used to search for `shortName`
  return COUNTRIES_BY_SHORT_NAME[shortName as CountryShortName] ?? COUNTRIES[shortName as CountryCode];
}

export function getCountryByCodeOrShortName(codeOrShortName?: string): TCountry | undefined {
  return COUNTRIES[codeOrShortName as CountryCode] ?? COUNTRIES_BY_SHORT_NAME[codeOrShortName as CountryShortName];
}

function convertString(input?: string): string {
  if (input === undefined) return '';
  return input
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('')
    .replace(/[^A-z]/g, '');
}

export function computeFlagIcon(country: Pick<TCountry, 'name'>) {
  return `icon-${convertString(country.name)}`;
}

export function computeFlagIconByCode(code?: string) {
  const country = getCountryByCode(code);
  return country
    ? computeFlagIcon(country)
    : undefined;
}
